/**
 * Unit conversion utilities for measurements
 *
 * All values are stored in metric units in the database:
 * - Length: centimeters (cm)
 * - Distance: kilometers (km)
 * - Weight: kilograms (kg)
 * - Volume: cubic meters (m3)
 * - Liquid Volume: liters (l)
 * - Area: square meters (m2)
 * - Temperature: celsius (c)
 */

import measurementUnitsConfig from '../../../shared-config/measurement_units.config.json';
import type { UnitCategory } from '../../utils/measurementUnits';

/**
 * These components spell the liquid-volume category `liquidVolume`; the config
 * key is `liquid_volume`. This is the only place the two vocabularies differ, so
 * the bridge is declared once here and imported by every consumer.
 */
export const CONFIG_CATEGORY = {
  length: 'length',
  distance: 'distance',
  weight: 'weight',
  volume: 'volume',
  liquidVolume: 'liquid_volume',
  area: 'area',
  temperature: 'temperature',
} as const satisfies Record<string, UnitCategory>;

/**
 * Compile-time exhaustiveness over the CONFIG's category list.
 *
 * `satisfies Record<string, UnitCategory>` only checks that each value present IS
 * a category — it says nothing about a category being ABSENT. So a category added
 * to measurement_units.config.json would type-check fine here and then simply
 * never appear in a picker (`MeasurementCategory` is `keyof typeof
 * CONFIG_CATEGORY`), with no compile error and nothing at runtime to notice. This
 * makes that omission a `tsc` failure naming the missing category.
 *
 * `check_measurement_unit_coverage.py` cannot cover it: that gate is unit-level,
 * and this is a category-level gap one layer up.
 */
type MissingFromConfigCategory = Exclude<
  UnitCategory,
  (typeof CONFIG_CATEGORY)[keyof typeof CONFIG_CATEGORY]
>;
export type ConfigCategoryIsExhaustive = MissingFromConfigCategory extends never
  ? true
  : { error: 'CONFIG_CATEGORY is missing a category'; missing: MissingFromConfigCategory };
export const CONFIG_CATEGORY_IS_EXHAUSTIVE: ConfigCategoryIsExhaustive = true;

// Conversion factors to the category's metric base unit — DERIVED from
// measurement_units.config.json, not typed here.
//
// They used to be a hand-written table in this file and nowhere else, which is
// why a US tenant read pounds on screen and kilograms on every printed
// document: the backend had no factor to print with. The config now declares
// `toMetricFactor` per unit and BOTH converters read it —
// `shared/utils/measurement_units.py` and this file — so screen and paper
// cannot disagree about what a pound is, and there is no second copy to drift.
// `tools/check_measurement_unit_coverage.py` refuses one in either file.
//
// A unit whose config entry declares no numeric factor is OMITTED rather than
// defaulted, so it falls through to the warn-and-1:1 path in `toMetric` below
// instead of white-screening the app on a config typo. Three things then fire
// on it: that console warning, the coverage test beside this file (which
// set-equals these keys against the config), and the static gate.
type ConfigCategories = typeof measurementUnitsConfig.categories;
type LinearConfigCategory = Exclude<keyof ConfigCategories, 'temperature'>;

function factorsFor<C extends LinearConfigCategory>(
  category: C
): Record<keyof ConfigCategories[C]['units'], number> {
  // Entries only, narrowed on the VALUE rather than on a key prefix: a group
  // may carry an `x-…-ok` escape sibling whose value is a plain string
  // (see .claude/rules/rules-config-enums.md).
  const units: Record<string, unknown> = measurementUnitsConfig.categories[category].units;
  const factors: Record<string, number> = {};
  for (const [code, spec] of Object.entries(units)) {
    if (typeof spec !== 'object' || spec === null) continue;
    const declared = (spec as { toMetricFactor?: unknown }).toMetricFactor;
    if (typeof declared === 'number') factors[code] = declared;
  }
  return factors as Record<keyof ConfigCategories[C]['units'], number>;
}

export const CONVERSION_TO_METRIC_BY_CATEGORY = {
  length: factorsFor('length'),
  distance: factorsFor('distance'),
  weight: factorsFor('weight'),
  volume: factorsFor('volume'),
  // The config spells this category `liquid_volume`; these components spell it
  // `liquidVolume` (CONFIG_CATEGORY above is the bridge).
  liquidVolume: factorsFor('liquid_volume'),
  area: factorsFor('area'),
};

type MeasurementCategory = keyof typeof CONVERSION_TO_METRIC_BY_CATEGORY | 'temperature';
type LengthUnit = keyof typeof CONVERSION_TO_METRIC_BY_CATEGORY.length;
type DistanceUnit = keyof typeof CONVERSION_TO_METRIC_BY_CATEGORY.distance;
type WeightUnit = keyof typeof CONVERSION_TO_METRIC_BY_CATEGORY.weight;
type VolumeUnit = keyof typeof CONVERSION_TO_METRIC_BY_CATEGORY.volume;
type LiquidVolumeUnit = keyof typeof CONVERSION_TO_METRIC_BY_CATEGORY.liquidVolume;
type AreaUnit = keyof typeof CONVERSION_TO_METRIC_BY_CATEGORY.area;
type TemperatureUnit = 'c' | 'f';

export type UnitType = LengthUnit | DistanceUnit | WeightUnit | VolumeUnit | LiquidVolumeUnit | AreaUnit | TemperatureUnit;

/**
 * Convert a value from a display unit to the metric storage unit
 */
export function toMetric(
  value: number | null,
  unit: UnitType,
  category: MeasurementCategory
): number | null {
  if (value === null) return null;

  // Temperature is special case
  if (category === 'temperature') {
    if (unit === 'f') {
      // Fahrenheit to Celsius: (F - 32) × 5/9
      return (value - 32) * 5 / 9;
    }
    return value; // Already Celsius
  }

  const conversionFactors = CONVERSION_TO_METRIC_BY_CATEGORY[category];
  const factor = conversionFactors[unit as keyof typeof conversionFactors];

  if (factor === undefined) {
    console.warn(`Unknown unit ${unit} for category ${category}`);
    return value;
  }

  return value * factor;
}

/**
 * Convert a value from metric storage unit to a display unit
 */
export function fromMetric(
  value: number | null,
  unit: UnitType,
  category: MeasurementCategory
): number | null {
  if (value === null) return null;

  // Temperature is special case
  if (category === 'temperature') {
    if (unit === 'f') {
      // Celsius to Fahrenheit: (C × 9/5) + 32
      return (value * 9 / 5) + 32;
    }
    return value; // Already Celsius
  }

  const conversionFactors = CONVERSION_TO_METRIC_BY_CATEGORY[category];
  const factor = conversionFactors[unit as keyof typeof conversionFactors];

  if (factor === undefined) {
    console.warn(`Unknown unit ${unit} for category ${category}`);
    return value;
  }

  return value / factor;
}

/**
 * Get the metric unit for a measurement category
 */
export function getMetricUnit(category: MeasurementCategory): UnitType {
  switch (category) {
    case 'length': return 'cm';
    case 'distance': return 'km';
    case 'weight': return 'kg';
    case 'volume': return 'm3';
    case 'liquidVolume': return 'l';
    case 'area': return 'm2';
    case 'temperature': return 'c';
  }
}

/**
 * Check if a unit is the metric unit for its category
 */
export function isMetricUnit(unit: UnitType, category: MeasurementCategory): boolean {
  return unit === getMetricUnit(category);
}
